// User-defined "calculated metrics" for KPI custom reports. Unlike the code-
// defined `computed` metrics (computed-metrics.ts), these are DATA-defined: the
// user builds a formula in the UI, it's stored as a structured expression tree
// in the report config, and it's evaluated per period bucket at render time.
//
// A calculated metric is referenced from a group's metricKeys as `calc.<id>`
// and its definition lives in `ReportConfig.calculatedMetrics` (report-scoped —
// NOT in the global METRIC_CATALOG). Operands may be ANY catalog metric key
// (every cube source plus `computed.*` and `custom.*`) as well as other
// `calc.<id>` metrics of the same report — nested references resolve through
// the report's defs at evaluation time, with cycles/dangling ids evaluating to
// null (see evaluateCalcExpr). Numeric constants round out the leaf kinds. No
// new cube queries are introduced: every operand resolves off datasets the
// dashboard already fetches.
//
// The expression is stored as a normalized AST (never a raw string), so the
// renderer walks it directly — there is no string parsing or `eval` at render
// time. `parseCalcExpr` is only the trust-boundary validator for the untrusted
// SavedReport.config JSON on the read path.

import type { MetricFormatType } from './types';

export const CALC_KEY_PREFIX = 'calc.';

// Cap on expression nesting depth — a depth guard against a maliciously deep
// (stack-overflowing) expression in the untrusted config blob.
export const MAX_EXPR_DEPTH = 32;

/** User-facing display format for a calculated metric. Maps to MetricFormatType at render. */
export type CalcFormat = 'currency' | 'number' | 'percent' | 'decimal';

export type CalcOperator = '+' | '-' | '*' | '/';

/**
 * Expression AST. Leaves are metric references (any catalog key, a `custom.<id>`
 * metric, or another `calc.<id>` metric of the same report) or numeric constants
 * (constants may be negative — negating a metric is `0 - x`); nodes are binary
 * operators (add, subtract, multiply, divide) with grouping expressed by nesting.
 */
export type CalcExpr =
  | { kind: 'ref'; key: string }
  | { kind: 'const'; value: number }
  | { kind: 'op'; op: CalcOperator; left: CalcExpr; right: CalcExpr };

export interface CalculatedMetricDef {
  id: string; // referenced from a group's metricKeys as `calc.<id>`
  name: string; // user label; never resolved from a dictionary
  format: CalcFormat; // display only — does NOT rescale the value (see evaluateCalcExpr)
  expression: CalcExpr;
}

export function isCalculatedMetricKey(key: string): boolean {
  return key.startsWith(CALC_KEY_PREFIX);
}

const CALC_FORMATS: ReadonlySet<string> = new Set<CalcFormat>(['currency', 'number', 'percent', 'decimal']);
const CALC_OPERATORS: ReadonlySet<string> = new Set<CalcOperator>(['+', '-', '*', '/']);

/**
 * Maps the user-facing format to the KPITable renderer's MetricFormatType.
 * `percent` → `percentage` (the renderer's %-appending branch); the rest pass
 * through unchanged (all are valid MetricFormatType values).
 */
export function calcFormatToMetricType(format: CalcFormat): MetricFormatType {
  return format === 'percent' ? 'percentage' : format;
}

/**
 * Structural validator for an untrusted expression tree. Returns a normalized
 * CalcExpr or null. Rejects unknown node kinds, non-arithmetic operators, empty
 * refs, non-finite constants, and trees deeper than MAX_EXPR_DEPTH. `calc.<id>`
 * refs are structurally valid (nesting is allowed); reference cycles and
 * dangling ids are a cross-definition concern handled at evaluation time
 * (evaluateCalcExpr resolves them to null), not here — a def must stay
 * individually valid even when a sibling def it references is dropped.
 */
export function parseCalcExpr(value: unknown, depth = 0): CalcExpr | null {
  if (depth > MAX_EXPR_DEPTH) return null;
  if (!value || typeof value !== 'object') return null;
  const node = value as Record<string, unknown>;

  if (node.kind === 'const') {
    return typeof node.value === 'number' && Number.isFinite(node.value) ? { kind: 'const', value: node.value } : null;
  }

  if (node.kind === 'ref') {
    if (typeof node.key !== 'string' || node.key.length === 0) return null;
    return { kind: 'ref', key: node.key };
  }

  if (node.kind === 'op') {
    if (typeof node.op !== 'string' || !CALC_OPERATORS.has(node.op)) return null;
    const left = parseCalcExpr(node.left, depth + 1);
    const right = parseCalcExpr(node.right, depth + 1);
    if (!left || !right) return null;
    return { kind: 'op', op: node.op as CalcOperator, left, right };
  }

  return null;
}

/** Validates one calculated-metric definition; returns null if malformed. */
export function parseCalculatedMetricDef(value: unknown): CalculatedMetricDef | null {
  if (!value || typeof value !== 'object') return null;
  const def = value as Record<string, unknown>;
  if (typeof def.id !== 'string' || def.id.length === 0) return null;
  if (typeof def.name !== 'string' || def.name.length === 0) return null;
  if (typeof def.format !== 'string' || !CALC_FORMATS.has(def.format)) return null;
  const expression = parseCalcExpr(def.expression);
  if (!expression) return null;
  return { id: def.id, name: def.name, format: def.format as CalcFormat, expression };
}

/**
 * Leniently validates a `calculatedMetrics` array: drops malformed defs and
 * keeps the good ones (a single bad calc must never blank an otherwise-valid
 * report). Returns [] for a non-array.
 */
export function parseCalculatedMetrics(value: unknown): CalculatedMetricDef[] {
  if (!Array.isArray(value)) return [];
  const out: CalculatedMetricDef[] = [];
  for (const item of value) {
    const def = parseCalculatedMetricDef(item);
    if (def) out.push(def);
  }
  return out;
}

/** Resolves a leaf reference key to its per-bucket value; may be null (e.g. a computed leaf). */
export type CalcRefGetter = (key: string) => number | null;

/** Calculated-metric defs keyed by their bare id (NOT the `calc.<id>` key). */
export type CalcDefsById = ReadonlyMap<string, CalculatedMetricDef>;

const EMPTY_DEFS: CalcDefsById = new Map();

/**
 * Budget on how deep a chain of nested `calc.<id>` references may go — the
 * cross-definition sibling of MAX_EXPR_DEPTH (which caps only one def's own
 * tree). The config blob is untrusted, so without this a chain of thousands of
 * one-line defs would overflow the stack. Far above anything a real report
 * builds; refs past the budget resolve to null.
 */
export const MAX_CALC_CHAIN_DEPTH = 64;

function evaluateWithAncestors(
  expr: CalcExpr,
  get: CalcRefGetter,
  defs: CalcDefsById,
  ancestors: ReadonlySet<string>,
  memo: Map<string, number | null>,
): number | null {
  switch (expr.kind) {
    case 'const':
      return expr.value;
    case 'ref': {
      if (expr.key.startsWith(CALC_KEY_PREFIX)) {
        const id = expr.key.slice(CALC_KEY_PREFIX.length);
        // Per-call memo: for a fixed getter a def's value is path-independent
        // (a def on a reference cycle is null under EVERY evaluation order,
        // because the cycle must be cut somewhere on its path and null
        // propagates through every operator), so diamonds keep both-branch
        // semantics while total work stays linear in the defs' AST sizes —
        // without this, chained defs like `c_k = calc.c_{k-1} + calc.c_{k-1}`
        // would cost 2^k evaluations.
        if (memo.has(id)) return memo.get(id) ?? null;
        // Cycle (id already on this evaluation path) or dangling id → null.
        // The ancestor set is per-path, so a diamond (two branches referencing
        // the same def) still evaluates both branches. Not memoized: a cycle
        // cut is not a completed evaluation of `id`.
        if (ancestors.has(id)) return null;
        // Chain budget exhausted → null (not memoized — a budget cut at this
        // depth says nothing about the def's value on a shallower path).
        if (ancestors.size >= MAX_CALC_CHAIN_DEPTH) return null;
        const def = defs.get(id);
        if (!def) return null;
        const value = evaluateWithAncestors(def.expression, get, defs, new Set(ancestors).add(id), memo);
        memo.set(id, value);
        return value;
      }
      return get(expr.key);
    }
    case 'op': {
      const left = evaluateWithAncestors(expr.left, get, defs, ancestors, memo);
      const right = evaluateWithAncestors(expr.right, get, defs, ancestors, memo);
      if (left === null || right === null) return null;
      switch (expr.op) {
        case '+':
          return left + right;
        case '-':
          return left - right;
        case '*':
          return left * right;
        case '/':
          return right === 0 ? null : left / right;
        default:
          return null;
      }
    }
    default:
      return null;
  }
}

/**
 * Evaluates the expression against a per-bucket getter. Null propagates: any
 * null operand → null, and division by zero (or by null) → null, so NaN /
 * Infinity never reach a table cell. The result is used AS-IS — the metric's
 * `format` only controls display (append %, prepend $), it does not rescale, so
 * a user wanting a percentage builds `x / y * 100`.
 *
 * `defs` resolves nested `calc.<id>` leaves against the report's other
 * calculated metrics; without it (or for a dangling id / reference cycle /
 * a chain deeper than MAX_CALC_CHAIN_DEPTH) a calc leaf evaluates to null.
 * Cycle detection tracks the ids on the current evaluation path only, so
 * shared sub-metrics (diamonds) are fine; resolved defs are memoized per call,
 * so evaluation cost is linear in the defs' total size.
 */
export function evaluateCalcExpr(expr: CalcExpr, get: CalcRefGetter, defs: CalcDefsById = EMPTY_DEFS): number | null {
  return evaluateWithAncestors(expr, get, defs, new Set(), new Map());
}

/** Unique leaf ref keys of a calculated-metric expression, in first-seen order. */
export function collectCalcRefKeys(expr: CalcExpr): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  const walk = (node: CalcExpr): void => {
    if (node.kind === 'ref') {
      if (!seen.has(node.key)) {
        seen.add(node.key);
        keys.push(node.key);
      }
      return;
    }
    if (node.kind === 'op') {
      walk(node.left);
      walk(node.right);
    }
  };
  walk(expr);
  return keys;
}

/**
 * Unique NON-calc leaf ref keys of an expression, expanding nested `calc.<id>`
 * refs through `defs` transitively (cycle-safe; dangling ids contribute
 * nothing). This is the "what does this formula ultimately read" view used for
 * loading gates and data-availability warnings.
 */
export function collectCalcLeafKeys(expr: CalcExpr, defs: CalcDefsById = EMPTY_DEFS): string[] {
  const keys: string[] = [];
  const seenKeys = new Set<string>();
  const seenIds = new Set<string>();
  const walk = (node: CalcExpr, chainDepth: number): void => {
    if (node.kind === 'ref') {
      if (node.key.startsWith(CALC_KEY_PREFIX)) {
        const id = node.key.slice(CALC_KEY_PREFIX.length);
        // A global visited set is correct here (unlike evaluation's per-path
        // ancestor set): leaf collection is path-independent. The chain budget
        // bounds stack depth against an untrusted config's def chains.
        if (seenIds.has(id) || chainDepth >= MAX_CALC_CHAIN_DEPTH) return;
        seenIds.add(id);
        const def = defs.get(id);
        if (def) walk(def.expression, chainDepth + 1);
        return;
      }
      if (!seenKeys.has(node.key)) {
        seenKeys.add(node.key);
        keys.push(node.key);
      }
      return;
    }
    if (node.kind === 'op') {
      walk(node.left, chainDepth);
      walk(node.right, chainDepth);
    }
  };
  walk(expr, 0);
  return keys;
}

/** Builds a CalcDefsById map from a defs list (later duplicates win, like Map). */
export function calcDefsById(defs: ReadonlyArray<CalculatedMetricDef>): CalcDefsById {
  return new Map(defs.map((def) => [def.id, def]));
}

/**
 * Ids of the defs whose expressions reference `targetId`, directly or through
 * other calc refs (plus `targetId` itself when self-referencing). Using any of
 * these as an operand of `targetId` would create a reference cycle — the
 * builder excludes them from the operand picker.
 */
export function calcIdsReferencing(targetId: string, defs: ReadonlyArray<CalculatedMetricDef>): Set<string> {
  const directRefs = new Map<string, string[]>();
  for (const def of defs) {
    directRefs.set(
      def.id,
      collectCalcRefKeys(def.expression)
        .filter((key) => key.startsWith(CALC_KEY_PREFIX))
        .map((key) => key.slice(CALC_KEY_PREFIX.length)),
    );
  }
  const referencing = new Set<string>();
  const reaches = (id: string, visited: Set<string>, chainDepth: number): boolean => {
    if (visited.has(id) || chainDepth >= MAX_CALC_CHAIN_DEPTH) return false;
    visited.add(id);
    for (const next of directRefs.get(id) ?? []) {
      if (next === targetId || reaches(next, visited, chainDepth + 1)) return true;
    }
    return false;
  };
  for (const def of defs) {
    if (reaches(def.id, new Set(), 0)) referencing.add(def.id);
  }
  return referencing;
}

/** Lightweight validity check for the builder's Save gating. */
export function isValidCalcExpr(expr: unknown): boolean {
  return parseCalcExpr(expr) !== null;
}
